// src/features/master-data/components/MasterDataFilters.js
import React, { useState } from 'react';
import { Search, Filter, X, ChevronDown } from 'lucide-react';
import Input from '../../../shared/components/Input';
import Button from '../../../shared/components/Button';

const MasterDataFilters = ({
  filters,
  onFilterChange,
  onReset,
  fields = [],
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState(filters.search || '');

  const handleSearch = (e) => {
    e.preventDefault();
    onFilterChange({ search: searchTerm });
  };

  const hasActiveFilters = () => {
    return Object.keys(filters).length > 0;
  };

  const getFilterFields = () => {
    return fields.filter(field => field.filterable !== false);
  };

  const filterFields = getFilterFields();

  return (
    <div className="bg-white rounded-xl shadow-sm p-4">
      {/* Search Bar */}
      <form onSubmit={handleSearch} className="flex gap-2">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
        <button
          type="submit"
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          Search
        </button>
        
        {filterFields.length > 0 && (
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
          >
            <Filter className="h-5 w-5" />
            Filters
            <ChevronDown className={`h-4 w-4 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
          </button>
        )}
        
        {hasActiveFilters() && (
          <button
            type="button"
            onClick={onReset}
            className="px-4 py-2 text-gray-600 hover:text-gray-900 transition-colors flex items-center gap-2"
          >
            <X className="h-5 w-5" />
            Clear
          </button>
        )}
      </form>

      {/* Expanded Filters */}
      {isExpanded && filterFields.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {filterFields.map((field) => (
              <div key={field.name}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {field.label}
                </label>
                
                {field.type === 'select' ? (
                  <select
                    value={filters[field.name] || ''}
                    onChange={(e) => onFilterChange({ [field.name]: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">All</option>
                    {field.options?.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : field.type === 'boolean' ? (
                  <select
                    value={filters[field.name] ?? ''}
                    onChange={(e) => onFilterChange({ [field.name]: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">All</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                ) : (
                  <Input
                    type={field.type || 'text'}
                    value={filters[field.name] || ''}
                    onChange={(e) => onFilterChange({ [field.name]: e.target.value })}
                    placeholder={`Filter by ${field.label.toLowerCase()}`}
                  />
                )}
              </div>
            ))}
          </div>

          <div className="flex justify-end mt-4">
            <Button
              variant="primary"
              size="sm"
              onClick={() => setIsExpanded(false)}
            >
              Apply Filters
            </Button>
          </div>
        </div>
      )}

      {/* Active Filter Tags */}
      {hasActiveFilters() && (
        <div className="mt-4 flex flex-wrap gap-2">
          {Object.entries(filters).map(([key, value]) => {
            if (!value || key === 'search') return null;
            const field = fields.find(f => f.name === key);
            return (
              <span
                key={key}
                className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-800"
              >
                {field?.label || key}: {value}
                <button
                  onClick={() => onFilterChange({ [key]: undefined })}
                  className="ml-2 hover:text-primary-600"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MasterDataFilters;